// WaitlistManagement.js — Super Admin Waitlist Dashboard
// 5 tabs: Overview, Cohorts & Engagement, Subscribers, Survey Analysis, Launch Operations

import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, TrendingUp, Mail, BarChart3, Send, Filter, Search, Download,
  ChevronLeft, ChevronRight, CheckCircle, XCircle, AlertTriangle, Rocket,
  Target, Zap, ArrowRight, Loader2
} from 'lucide-react';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { CHART, CHART_SERIES } from '../../../config/statusColors';
import { getStatusStyle } from '../../../config/statusColors';
import api from '../../../services/api';

// ─── Shared components ─────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, subtitle }) => (
  <div className="bg-white border border-zinc-200 p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{title}</p>
        <p className="text-2xl font-bold text-black mt-1 font-sans">{value}</p>
        {subtitle && <p className="text-xs text-zinc-500 mt-1 font-sans">{subtitle}</p>}
      </div>
      <div className="p-2 bg-zinc-100"><Icon className="w-5 h-5 text-zinc-600" /></div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const style = getStatusStyle(status);
  return (
    <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" style={style}>
      {status}
    </span>
  );
};

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] border-b-2 transition-colors ${
      active ? 'border-[#4D4D4D] text-black' : 'border-transparent text-zinc-400 hover:text-zinc-600'
    }`}
  >
    {children}
  </button>
);

// ─── Main Component ────────────────────────────────────────────────────
const WaitlistManagement = () => {
  const [tab, setTab] = useState('overview');
  const [subscribers, setSubscribers] = useState([]);
  const [analytics, setAnalytics] = useState({ total: 0, this_week: 0, signed_up: 0, invited: 0, onboarded: 0, avg_fit_score: 0 });
  const [growth, setGrowth] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [engagement, setEngagement] = useState([]);
  const [themes, setThemes] = useState([]);
  const [funnel, setFunnel] = useState({ subscribed: 0, invited: 0, signed_up: 0, onboarded: 0, active_7d: 0, active_30d: 0 });
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscriber tab state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [waitlistFilter, setWaitlistFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [subscribersLoading, setSubscribersLoading] = useState(false);

  // Batch invite state
  const [batchName, setBatchName] = useState('');
  const [batchSize, setBatchSize] = useState(10);
  const [batchVariant, setBatchVariant] = useState('');
  const [batchMinFitScore, setBatchMinFitScore] = useState(0);
  const [batchSource, setBatchSource] = useState('');

  // Priority weight sliders
  const [wSignupDate, setWSignupDate] = useState(50);
  const [wReferralCount, setWReferralCount] = useState(30);
  const [wFitScore, setWFitScore] = useState(70);

  // Server-side subscriber fetch with search, filters, and pagination
  const fetchSubscribers = async (pg = page) => {
    setSubscribersLoading(true);
    try {
      const params = { page: pg, limit: 50 };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (waitlistFilter) params.waitlist_opted_in = waitlistFilter;
      const subRes = await api.getWaitlistSubscribers(params);
      if (subRes.success) {
        setSubscribers(subRes.subscribers);
        setPagination(subRes.pagination || { total: subRes.subscribers.length, pages: 1 });
      }
    } catch (err) {
      console.error('Failed to load waitlist subscribers:', err);
    }
    setSubscribersLoading(false);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [anRes, grRes, coRes, enRes, thRes, fuRes] = await Promise.allSettled([
          api.getWaitlistAnalytics(),
          api.getWaitlistGrowth('day'),
          api.getWaitlistCohorts(),
          api.getWaitlistEngagement(),
          api.getWaitlistSurveyThemes(),
          api.getWaitlistFunnel(),
        ]);
        if (anRes.status === 'fulfilled' && anRes.value.success) setAnalytics(anRes.value);
        if (grRes.status === 'fulfilled' && grRes.value.success) setGrowth(grRes.value.growth);
        if (coRes.status === 'fulfilled' && coRes.value.success) setCohorts(coRes.value.cohorts);
        if (enRes.status === 'fulfilled' && enRes.value.success) setEngagement(enRes.value.engagement);
        if (thRes.status === 'fulfilled' && thRes.value.success) setThemes(thRes.value.themes);
        if (fuRes.status === 'fulfilled' && fuRes.value.success) setFunnel(fuRes.value.funnel);
      } catch (err) {
        console.error('Failed to load waitlist analytics:', err);
      }

      await fetchSubscribers(1);
    };
    load().finally(() => setLoading(false));
  }, []);

  // Re-fetch when filters or page change (debounced for search)
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchSubscribers(page);
    }, searchTerm ? 400 : 0);
    return () => clearTimeout(timeout);
  }, [searchTerm, statusFilter, sourceFilter, waitlistFilter, page]);

  // ─── Computed growth chart data (stacked by source) ───
  const growthChartData = useMemo(() => {
    const byPeriod = {};
    growth.forEach(r => {
      if (!byPeriod[r.period]) byPeriod[r.period] = { period: r.period };
      byPeriod[r.period][r.source] = (byPeriod[r.period][r.source] || 0) + r.count;
    });
    return Object.values(byPeriod).sort((a, b) => a.period.localeCompare(b.period)).slice(-30);
  }, [growth]);

  const sources = useMemo(() => [...new Set(growth.map(r => r.source))], [growth]);

  // Source pie data
  const sourcePieData = useMemo(() => {
    const counts = {};
    subscribers.forEach(s => { counts[s.source] = (counts[s.source] || 0) + 1; });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [subscribers]);

  // Subscribers are now fetched server-side with filters applied
  const totalPages = pagination.pages || 1;
  const pagedSubscribers = subscribers;

  // Selection helpers
  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pagedSubscribers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pagedSubscribers.map(s => s.id)));
    }
  };

  // Bulk invite
  const handleBulkInvite = async () => {
    for (const id of selectedIds) {
      try { await api.inviteWaitlistSubscriber(id); } catch { /* skip */ }
    }
    setSelectedIds(new Set());
    fetchSubscribers(page);
  };

  // CSV export
  const handleExportCSV = () => {
    const headers = ['email', 'phone', 'source', 'status', 'waitlist_opted_in', 'fit_score', 'referral_count', 'created_at'];
    const rows = subscribers.map(s => headers.map(h => s[h] ?? ''));
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'waitlist_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Batch invite handler
  const handleBatchInvite = async () => {
    try {
      const result = await api.batchInviteWaitlist({
        name: batchName || `Batch ${new Date().toISOString().slice(0, 10)}`,
        criteria: {
          min_fit_score: batchMinFitScore || undefined,
          source: batchSource || undefined,
          order_weights: { signup_date: wSignupDate, referral_count: wReferralCount, fit_score: wFitScore },
        },
        size: batchSize,
        variant: batchVariant || undefined,
      });
      if (result.success) {
        setBatches(prev => [{ id: result.batchId, name: batchName, size: result.invited, variant: batchVariant, sent_at: new Date().toISOString().slice(0, 10), activation_rate: 0 }, ...prev]);
        setBatchName('');
      }
    } catch (err) {
      console.error('Batch invite error:', err);
    }
  };

  const conversionRate = analytics.total > 0 ? ((analytics.signed_up / analytics.total) * 100).toFixed(1) : '0';

  const inputClass = 'px-3 py-2 border border-zinc-200 text-xs font-mono text-black bg-white focus:outline-none focus:border-[#4D4D4D] transition-colors w-full';
  const sectionHeader = 'font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3';

  return (
    <div className="p-6 bg-zinc-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-black font-sans">Waitlist Management</h1>
        <p className="text-sm text-zinc-500 font-sans mt-1">Subscriber pipeline, analytics, and launch operations</p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-zinc-400 animate-spin" />
          <span className="ml-3 font-mono text-xs text-zinc-500">Loading waitlist data…</span>
        </div>
      )}

      {!loading && <>
      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200 mb-6">
        <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>Overview</TabButton>
        <TabButton active={tab === 'cohorts'} onClick={() => setTab('cohorts')}>Cohorts & Engagement</TabButton>
        <TabButton active={tab === 'subscribers'} onClick={() => setTab('subscribers')}>Subscribers</TabButton>
        <TabButton active={tab === 'survey'} onClick={() => setTab('survey')}>Survey Analysis</TabButton>
        <TabButton active={tab === 'launch'} onClick={() => setTab('launch')}>Launch Operations</TabButton>
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* OVERVIEW TAB */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard title="Total Subscribers" value={analytics.total} icon={Users} subtitle={`${analytics.invited} invited`} />
            <StatCard title="This Week" value={analytics.this_week} icon={TrendingUp} subtitle="New signups" />
            <StatCard title="Conversion Rate" value={`${conversionRate}%`} icon={Target} subtitle={`${analytics.signed_up} signed up`} />
            <StatCard title="Avg Fit Score" value={analytics.avg_fit_score || 0} icon={Zap} subtitle="Out of 100" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Growth chart */}
            <div className="lg:col-span-2 bg-white border border-zinc-200 p-5">
              <p className={sectionHeader}>Signup Growth</p>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={growthChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="period" tick={{ fontSize: 9, fontFamily: 'monospace', fill: '#a1a1aa' }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fontSize: 9, fontFamily: 'monospace', fill: '#a1a1aa' }} />
                  <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'monospace' }} />
                  <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'monospace' }} />
                  {sources.map((src, i) => (
                    <Area key={src} type="monotone" dataKey={src} stackId="1" stroke={CHART_SERIES[i % CHART_SERIES.length]} fill={CHART_SERIES[i % CHART_SERIES.length]} fillOpacity={0.4} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Source pie chart */}
            <div className="bg-white border border-zinc-200 p-5">
              <p className={sectionHeader}>Source Distribution</p>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={sourcePieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#a1a1aa', strokeWidth: 1 }}
                  >
                    {sourcePieData.map((_, i) => <Cell key={i} fill={CHART_SERIES[i % CHART_SERIES.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'monospace' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* COHORTS & ENGAGEMENT TAB */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {tab === 'cohorts' && (
        <>
          {/* Cohort table */}
          <div className="bg-white border border-zinc-200 p-5 mb-6">
            <p className={sectionHeader}>Cohort Analysis (by Signup Week)</p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    {['Week', 'Size', 'Opened', 'Clicked', 'Converted', 'Warmth'].map(h => (
                      <th key={h} className="px-4 py-2 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {cohorts.map(c => {
                    const warmth = c.size > 0 ? Math.round((c.converted / c.size) * 100) : 0;
                    const warmthColor = warmth >= 30 ? '#3832e3' : warmth >= 15 ? '#a1a1aa' : '#e4e4e7';
                    return (
                      <tr key={c.cohort_week} className="hover:bg-zinc-50">
                        <td className="px-4 py-3 font-mono text-xs text-black">{c.cohort_week}</td>
                        <td className="px-4 py-3 font-mono text-xs text-black font-bold">{c.size}</td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-600">{c.opened}</td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-600">{c.clicked}</td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-600">{c.converted}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-zinc-100 h-2">
                              <div className="h-2" style={{ width: `${warmth}%`, backgroundColor: warmthColor }} />
                            </div>
                            <span className="font-mono text-[10px] text-zinc-500">{warmth}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {cohorts.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-zinc-400 font-mono">No cohort data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Engagement decay chart */}
          <div className="bg-white border border-zinc-200 p-5">
            <p className={sectionHeader}>Engagement Decay (by Weeks Since Signup)</p>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={engagement}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="week_offset" tick={{ fontSize: 9, fontFamily: 'monospace', fill: '#a1a1aa' }} tickFormatter={v => `W${v}`} />
                <YAxis tick={{ fontSize: 9, fontFamily: 'monospace', fill: '#a1a1aa' }} tickFormatter={v => `${v}%`} />
                <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'monospace' }} formatter={v => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'monospace' }} />
                <Line type="monotone" dataKey="open_rate" stroke={CHART.water.stroke} strokeWidth={2} name="Open Rate" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="click_rate" stroke={CHART.goldenrod.stroke} strokeWidth={2} name="Click Rate" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SUBSCRIBERS TAB */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {tab === 'subscribers' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
                placeholder="Search email or phone..."
                className="pl-9 pr-3 py-2 border border-zinc-200 text-xs font-mono w-full bg-white focus:outline-none focus:border-[#4D4D4D]"
              />
            </div>
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className={inputClass + ' !w-auto'}>
              <option value="">All statuses</option>
              {['subscribed', 'invited', 'signed_up', 'onboarded', 'churned'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(1); }} className={inputClass + ' !w-auto'}>
              <option value="">All sources</option>
              {['website', 'demo_registration', 'referral'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={waitlistFilter} onChange={e => { setWaitlistFilter(e.target.value); setPage(1); }} className={inputClass + ' !w-auto'}>
              <option value="">Waitlist: All</option>
              <option value="true">Waitlist: Opted In</option>
              <option value="false">Waitlist: Not Opted In</option>
            </select>
            {subscribersLoading && <Loader2 className="w-4 h-4 text-zinc-400 animate-spin self-center" />}
          </div>

          {/* Bulk actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 mb-4 bg-white border border-zinc-200 px-4 py-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{selectedIds.size} selected</span>
              <button onClick={handleBulkInvite} className="px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] bg-[#4D4D4D] text-white hover:bg-black transition-colors">
                Invite Selected
              </button>
              <button onClick={handleExportCSV} className="px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] border border-zinc-300 text-black hover:border-[#4D4D4D] transition-colors flex items-center gap-1">
                <Download className="w-3 h-3" /> Export CSV
              </button>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox" checked={selectedIds.size === pagedSubscribers.length && pagedSubscribers.length > 0} onChange={toggleSelectAll} className="w-3.5 h-3.5" />
                  </th>
                  {['Email', 'Phone', 'Source', 'Status', 'Waitlist', 'Fit', 'Refs', 'Date', 'Flags'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {pagedSubscribers.map(s => (
                  <tr key={s.id} className={`hover:bg-zinc-50 ${selectedIds.has(s.id) ? 'bg-blue-50/30' : ''}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} className="w-3.5 h-3.5" />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-black">{s.email}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{s.phone_e164 || '—'}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-600">{s.source}</td>
                    <td className="px-3 py-2"><StatusBadge status={s.status} /></td>
                    <td className="px-3 py-2 text-center">
                      {s.waitlist_opted_in ? (
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-600 inline-block" />
                      ) : (
                        <span className="text-[10px] text-zinc-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-black text-center">{s.fit_score}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-500 text-center">{s.referral_count}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-zinc-400">{new Date(s.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        {s.is_duplicate && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[9px] font-mono uppercase">DUP</span>}
                        {s.is_bot_suspect && <span className="px-1.5 py-0.5 bg-red-50 text-red-700 text-[9px] font-mono uppercase">BOT</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {pagedSubscribers.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-xs text-zinc-400 font-mono">No subscribers yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <span className="font-mono text-[10px] text-zinc-500">{pagination.total} subscribers</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-1 border border-zinc-200 disabled:opacity-30 hover:border-zinc-400">
                <ChevronLeft className="w-4 h-4 text-zinc-600" />
              </button>
              <span className="font-mono text-[10px] text-zinc-500">{page} / {totalPages || 1}</span>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="p-1 border border-zinc-200 disabled:opacity-30 hover:border-zinc-400">
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SURVEY ANALYSIS TAB */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {tab === 'survey' && (
        <>
          {/* Top keyword stat blocks */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {themes.slice(0, 4).map(t => (
              <div key={t.keyword} className="bg-white border border-zinc-200 p-4 text-center">
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">#{t.keyword}</p>
                <p className="text-xl font-bold text-black font-sans">{t.count}</p>
                <p className="text-[10px] text-zinc-400 font-mono">mentions</p>
              </div>
            ))}
          </div>

          {/* Horizontal bar chart of keyword frequencies */}
          <div className="bg-white border border-zinc-200 p-5 mb-6">
            <p className={sectionHeader}>Keyword Frequency</p>
            <ResponsiveContainer width="100%" height={Math.max(300, themes.length * 28)}>
              <BarChart data={themes} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis type="number" tick={{ fontSize: 9, fontFamily: 'monospace', fill: '#a1a1aa' }} />
                <YAxis type="category" dataKey="keyword" tick={{ fontSize: 10, fontFamily: 'monospace', fill: '#52525b' }} width={70} />
                <Tooltip contentStyle={{ fontSize: 11, fontFamily: 'monospace' }} />
                <Bar dataKey="count" fill={CHART.water.stroke} radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Searchable response browser */}
          <div className="bg-white border border-zinc-200 p-5">
            <p className={sectionHeader}>Survey Responses</p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {subscribers.filter(s => s.survey_response).map(s => (
                <div key={s.id} className="border-b border-zinc-100 pb-2">
                  <p className="font-mono text-[10px] text-zinc-400">{s.email} · {new Date(s.created_at).toLocaleDateString()}</p>
                  <p className="text-xs text-zinc-700 font-sans mt-1">{s.survey_response}</p>
                </div>
              ))}
              {subscribers.filter(s => s.survey_response).length === 0 && (
                <p className="text-xs text-zinc-400 font-mono text-center py-8">No survey responses yet</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* LAUNCH OPERATIONS TAB */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {tab === 'launch' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Batch invite panel */}
            <div className="bg-white border border-zinc-200 p-5">
              <p className={sectionHeader}>Batch Invite</p>
              <div className="space-y-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">Batch Name</label>
                  <input value={batchName} onChange={e => setBatchName(e.target.value)} placeholder="e.g. Early Adopters Wave 3" className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">Size</label>
                    <input type="number" min={1} value={batchSize} onChange={e => setBatchSize(parseInt(e.target.value) || 1)} className={inputClass} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">Variant</label>
                    <input value={batchVariant} onChange={e => setBatchVariant(e.target.value)} placeholder="A" className={inputClass} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">Min Fit Score</label>
                    <input type="number" min={0} max={100} value={batchMinFitScore} onChange={e => setBatchMinFitScore(parseInt(e.target.value) || 0)} className={inputClass} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 block">Source Filter</label>
                    <select value={batchSource} onChange={e => setBatchSource(e.target.value)} className={inputClass}>
                      <option value="">All sources</option>
                      {[...new Set(subscribers.map(s => s.source))].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <button onClick={handleBatchInvite}
                  className="w-full py-2.5 bg-[#4D4D4D] text-white font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-black transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="w-3.5 h-3.5" /> Send Batch Invite
                </button>
              </div>
            </div>

            {/* Prioritization weight sliders */}
            <div className="bg-white border border-zinc-200 p-5">
              <p className={sectionHeader}>Prioritization Weights</p>
              <div className="space-y-5 mt-2">
                {[
                  { label: 'Signup Date (earliest first)', value: wSignupDate, setter: setWSignupDate },
                  { label: 'Referral Count', value: wReferralCount, setter: setWReferralCount },
                  { label: 'Fit Score', value: wFitScore, setter: setWFitScore },
                ].map(({ label, value, setter }) => (
                  <div key={label}>
                    <div className="flex justify-between mb-1">
                      <span className="font-mono text-[10px] text-zinc-600">{label}</span>
                      <span className="font-mono text-[10px] text-black font-bold">{value}</span>
                    </div>
                    <input type="range" min={0} max={100} value={value} onChange={e => setter(parseInt(e.target.value))}
                      className="w-full h-1 bg-zinc-200 appearance-none cursor-pointer accent-[#4D4D4D]"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Funnel visualization */}
          <div className="bg-white border border-zinc-200 p-5 mb-6">
            <p className={sectionHeader}>Conversion Funnel</p>
            <div className="flex items-center justify-between mt-4 overflow-x-auto">
              {[
                { label: 'Subscribed', count: funnel.subscribed, color: CHART_SERIES[0] },
                { label: 'Invited', count: funnel.invited, color: CHART_SERIES[1] },
                { label: 'Signed Up', count: funnel.signed_up, color: CHART_SERIES[2] },
                { label: 'Onboarded', count: funnel.onboarded, color: CHART_SERIES[3] },
                { label: 'Active 7d', count: funnel.active_7d, color: CHART_SERIES[4] },
                { label: 'Active 30d', count: funnel.active_30d, color: CHART_SERIES[5] },
              ].map((stage, i, arr) => (
                <React.Fragment key={stage.label}>
                  <div className="text-center min-w-[80px]">
                    <div className="w-16 mx-auto mb-2 rounded" style={{ height: `${Math.max(20, (stage.count / (funnel.subscribed || 1)) * 80)}px`, backgroundColor: stage.color }} />
                    <p className="font-sans text-lg font-bold text-black">{stage.count}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500">{stage.label}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="flex flex-col items-center px-1">
                      <ArrowRight className="w-4 h-4 text-zinc-300" />
                      <span className="font-mono text-[9px] text-zinc-400">
                        {stage.count > 0 ? `${Math.round((arr[i + 1].count / stage.count) * 100)}%` : '—'}
                      </span>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Batch history */}
          <div className="bg-white border border-zinc-200 p-5">
            <p className={sectionHeader}>Batch History</p>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    {['Name', 'Size', 'Variant', 'Sent', 'Activation'].map(h => (
                      <th key={h} className="px-4 py-2 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {batches.map(b => (
                    <tr key={b.id} className="hover:bg-zinc-50">
                      <td className="px-4 py-3 font-mono text-xs text-black">{b.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-600">{b.size}</td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">{b.variant || '—'}</td>
                      <td className="px-4 py-3 font-mono text-[10px] text-zinc-400">{b.sent_at}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-zinc-100 h-2">
                            <div className="h-2 bg-[#3832e3]" style={{ width: `${b.activation_rate}%` }} />
                          </div>
                          <span className="font-mono text-[10px] text-zinc-500">{b.activation_rate}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {batches.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-zinc-400 font-mono">No batches yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      </>}
    </div>
  );
};

export default WaitlistManagement;
